/* Shared select enhancement: native values and prices remain authoritative.
   Returns a cleanup function for section removal and reinitialization. */
(() => {
  if (window.asenmoOptionMenu) return;
  const active = new WeakMap();
  const openMenus = new Set();
  let originalAnchor, originalBodyAnchor, anchorFrame;
  const pauseAnchoring = () => {
    if (anchorFrame) { cancelAnimationFrame(anchorFrame); anchorFrame = null; }
    if (originalAnchor === undefined) { originalAnchor = document.documentElement.style.overflowAnchor; originalBodyAnchor = document.body.style.overflowAnchor; }
    document.documentElement.style.overflowAnchor = 'none';
    document.body.style.overflowAnchor = 'none';
  };
  const restoreAnchoring = () => {
    if (openMenus.size || originalAnchor === undefined || anchorFrame) return;
    const restore = () => {
      anchorFrame = null;
      if (openMenus.size) return;
      document.documentElement.style.overflowAnchor = originalAnchor;
      document.body.style.overflowAnchor = originalBodyAnchor;
      originalAnchor = undefined;
    };
    if (typeof requestAnimationFrame === 'function') anchorFrame = requestAnimationFrame(restore);
    else restore();
  };
  // Native selects remain the source of variant IDs/prices; enhance only the inline presentation.
  function enhance(select, {listHost} = {}) {
    if (active.has(select)) return active.get(select);
    const controller = new AbortController();
    const listener = { signal: controller.signal };
    const wrapper = document.createElement('div');
    wrapper.className = 'set-offer__option-picker';
    const trigger = document.createElement('button');
    trigger.type = 'button';
    trigger.className = 'set-offer__option-trigger';
    trigger.setAttribute('aria-haspopup', 'listbox');
    trigger.setAttribute('aria-expanded', 'false');
    const text = document.createElement('span');
    const arrow = document.createElement('span');
    arrow.className = 'set-offer__option-arrow';
    arrow.textContent = '⌄';
    arrow.setAttribute('aria-hidden', 'true');
    trigger.append(text, arrow);
    const list = document.createElement('div');
    list.className = 'set-offer__option-list';
    list.id = `${select.id}-choices`;
    list.setAttribute('role', 'listbox');
    const label = select.getAttribute('aria-label') || select.labels?.[0]?.textContent.trim() || 'Choose an option';
    list.setAttribute('aria-label', label);
    trigger.setAttribute('aria-controls', list.id);
    list.hidden = true;
    const options = [...select.options].filter(option => option.value).map(option => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'set-offer__option';
      button.setAttribute('role', 'option');
      button.textContent = option.textContent;
      button.disabled = option.disabled;
      button.dataset.value = option.value;
      button.addEventListener('click', () => {
        if (button.disabled || select.disabled) return;
        select.value = option.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        close(true);
      }, listener);
      list.append(button);
      return button;
    });
    const sync = () => {
      text.textContent = select.selectedOptions[0]?.textContent || 'Choose an option';
      trigger.setAttribute('aria-label', `${label}: ${text.textContent}`);
      trigger.disabled = select.disabled;
      options.forEach(button => {
        button.setAttribute('aria-selected', String(button.dataset.value === select.value));
        button.disabled = select.disabled || [...select.options].find(option => option.value === button.dataset.value)?.disabled;
      });
      if (select.disabled) close();
    };
    const close = (focus = false) => {
      if (!list.hidden) pauseAnchoring();
      openMenus.delete(close);
      list.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      if (focus) trigger.focus({ preventScroll: true });
      restoreAnchoring();
    };
    const open = () => {
      if (select.disabled) return;
      openMenus.forEach(other => { if (other !== close) other(); });
      pauseAnchoring();
      openMenus.add(close);
      const position = {top: window.scrollY, left: window.scrollX};
      list.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      (options.find(button => button.dataset.value === select.value && !button.disabled) || options.find(button => !button.disabled))?.focus({ preventScroll: true });
      if (window.scrollY !== position.top || window.scrollX !== position.left) window.scrollTo({...position, behavior: 'instant'});
    };
    trigger.addEventListener('click', () => list.hidden ? open() : close(), listener);
    const onKeydown = event => {
      if (event.key === 'Escape' && !list.hidden) { event.preventDefault(); close(true); return; }
      if (event.key === 'Tab' && !list.hidden) { close(true); return; }
      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (list.hidden) { open(); return; }
      const enabled = options.filter(button => !button.disabled);
      const index = enabled.indexOf(document.activeElement);
      const next = event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + enabled.length) % enabled.length;
      enabled[next]?.focus({ preventScroll: true });
    };
    wrapper.addEventListener('keydown', onKeydown, listener);
    if (listHost) list.addEventListener('keydown', onKeydown, listener);
    const outside = event => { if (!wrapper.contains(event.target) && !list.contains(event.target)) close(); };
    document.addEventListener('click', outside, listener);
    select.addEventListener('change', sync, listener);
    select.replaceWith(wrapper);
    wrapper.append(select, trigger);
    (listHost || wrapper).append(list);
    select.hidden = true;
    sync();
    const observer = new MutationObserver(sync);
    observer.observe(select, { attributes: true, attributeFilter: ['disabled'], subtree: true });
    const cleanup = () => {
      close();
      controller.abort();
      observer.disconnect();
      active.delete(select);
      document.removeEventListener('click', outside);
      select.removeEventListener('change', sync);
      select.hidden = false;
      list.remove();
      wrapper.replaceWith(select);
    };
    active.set(select, cleanup);
    return cleanup;
  }
  window.asenmoOptionMenu = enhance;
})();
